<template>
  <b-modal :title="helper.trans('labels.webhooks_history')"
           dialog-class="modal-xl"
           :modelValue="webhooksModal"
           footer-class="d-flex justify-content-end"
           @update:modelValue="webhooksModal = $event">
    <template #default>
      <LoaderWrapper ref="webhooksModalLoader">
        <div v-if="webhooks.length === 0" class="text-center text-muted py-4">
          {{ helper.trans('labels.no_webhooks_found') }}
        </div>
        <div v-else class="overflow-auto">
          <table class="table table-striped table-sm">
            <thead>
            <tr>
              <th>ID</th>
              <th>{{ helper.trans('labels.url') }}</th>
              <th>{{ helper.trans('labels.initiator') }}</th>
              <th>{{ helper.trans('labels.status') }}</th>
              <th>{{ helper.trans('labels.retry') }}</th>
              <th>{{ helper.trans('labels.response_code') }}</th>
              <th>{{ helper.trans('labels.created_at') }}</th>
              <th>{{ helper.trans('labels.sent_at') }}</th>
              <th>{{ helper.trans('labels.notify_at') }}</th>
              <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="webhook in webhooks" :key="webhook.id">
              <td>{{ webhook.id }}</td>
              <td class="text-truncate" style="max-width: 200px;" :title="webhook.url">{{ webhook.url }}</td>
              <td>{{ getInitiatorDisplay(webhook) }}</td>
              <td>
                {{ getWebhookStatusText(webhook.status) }}
              </td>
              <td>{{ webhook.retry }}</td>
              <td>
                <span v-if="webhook.responseCode">{{ webhook.responseCode }}</span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>{{ dateHelper.format(webhook.createdAt) }}</td>
              <td>
                <span v-if="webhook.sentAt">{{ dateHelper.format(webhook.sentAt) }}</span>
                <span v-else class="text-muted">-</span>
              </td>
              <td>{{ dateHelper.format(webhook.notifyAt) }}</td>
              <td>
                <button
                  type="button"
                  @click="showWebhookDetails(webhook)"
                  class="btn btn-outline-secondary btn-sm"
                  :title="helper.trans('labels.show_details')"
                >
                  <i class="fa fa-eye"></i>
                </button>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" @click.prevent="webhooksModal = false" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.close')  }}
      </button>
    </template>
  </b-modal>
  <b-modal :title="helper.trans('labels.webhook_details')"
           dialog-class="modal-lg"
           :modelValue="webhookDetailsModal"
           footer-class="d-flex justify-content-end"
           @update:modelValue="webhookDetailsModal = $event">
    <template #default>
      <div v-if="selectedWebhook">
        <div class="row mb-3">
          <div class="col-3"><strong>{{ helper.trans('labels.url') }}:</strong></div>
          <div class="col-9">{{ selectedWebhook.url }}</div>
        </div>

        <div class="row mb-3" v-if="selectedWebhook.token">
          <div class="col-3"><strong>{{ helper.trans('labels.token') }}:</strong></div>
          <div class="col-9">{{ selectedWebhook.token }}</div>
        </div>

        <div class="row mb-3" v-if="selectedWebhook.headers">
          <div class="col-3"><strong>{{ helper.trans('labels.headers') }}:</strong></div>
          <div class="col-9">
            <pre class="bg-light p-2 rounded small">{{ JSON.stringify(selectedWebhook.headers, null, 2) }}</pre>
          </div>
        </div>

        <div class="row mb-3">
          <div class="col-3"><strong>{{ helper.trans('labels.payload') }}:</strong></div>
          <div class="col-9">
            <pre class="bg-light p-2 rounded small">{{ JSON.stringify(selectedWebhook.payload, null, 2) }}</pre>
          </div>
        </div>

        <div class="row mb-3" v-if="selectedWebhook.responseBody">
          <div class="col-3"><strong>{{ helper.trans('labels.response_body') }}:</strong></div>
          <div class="col-9">
            <pre class="bg-light p-2 rounded small">{{ selectedWebhook.responseBody }}</pre>
          </div>
        </div>
      </div>
    </template>
    <template #footer>
      <button type="button" @click.prevent="webhookDetailsModal = false" class="btn btn-outline-dark btn-sm text-capitalize-first">
        {{ helper.trans('button.close')  }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">

import {computed, defineComponent, ref} from 'vue'
import {reactive, toRefs} from "@vue/reactivity";
import {BModal} from "bootstrap-vue-3";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import {helper} from "@/service/CommonHelper";
import {api} from "@/service/APIClient";
import WebhookDTO from "@/models/WebhookDTO";
import UserDTO from "@/models/UserDTO";
import PaginationDTO from "@/models/PaginationDTO";
import {useNotification} from "@kyvg/vue3-notification";
import {dateHelper} from "@/service/DateHelper";

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper
  },
  setup() {
    const webhooksModalLoader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const notification = useNotification()

    const state = reactive({
      webhooksModal: false,
      webhookDetailsModal: false,
      webhooks: [] as WebhookDTO[],
      selectedWebhook: null as WebhookDTO|null,
      webhookInitiatorsMap: {} as Record<string, UserDTO>,
    });

    const show = async (invoiceId: string) => {

      state.webhooksModal = true;
      webhooksModalLoader.value?.show();

      try {
        state.webhooks = await api.webhooksInvoice(invoiceId);
        const manualInitiatorIds = [...new Set(
          state.webhooks
            .filter(w => w.initiatorType === 'manual' && w.initiatorId)
            .map(w => w.initiatorId as string)
        )];
        if (manualInitiatorIds.length > 0) {
          const ids = manualInitiatorIds.map(id => Number(id)).filter(n => !isNaN(n));
          const res = await api.users({ limit: ids.length, offset: 0 } as PaginationDTO, { ids });
          state.webhookInitiatorsMap = {};
          for (const u of res.items) {
            state.webhookInitiatorsMap[String(u.id)] = u;
          }
        } else {
          state.webhookInitiatorsMap = {};
        }
      } catch (error) {
        notification.notify({
          type: 'error',
          title: helper.trans('server_errors.failed_to_load_webhooks', {}, 'Failed to load webhooks')
        });
      } finally {
        webhooksModalLoader.value?.hide();
      }
    }


    const showWebhookDetails = (webhook: WebhookDTO) => {
      state.selectedWebhook = webhook;
      state.webhookDetailsModal = true;
    }

    const getWebhookStatusText = (status: number) => {
      return helper.trans(`webhook_status.${status}`);
    }

    const getInitiatorDisplay = (webhook: WebhookDTO) => {
      if (webhook.initiatorType === 'manual') {
        const label = helper.trans('labels.webhook_initiator_manual');
        if (webhook.initiatorId && state.webhookInitiatorsMap[webhook.initiatorId]) {
          const u = state.webhookInitiatorsMap[webhook.initiatorId];
          return `${label} (${u.name || u.email || webhook.initiatorId})`;
        }
        if (webhook.initiatorId) return `${label} (ID: ${webhook.initiatorId})`;
        return label;
      }
      if (webhook.initiatorType === 'auto') {
        return helper.trans('labels.webhook_initiator_auto');
      }
      return '—';
    }

    return {
      ...toRefs(state),
      show,
      dateHelper,
      webhooksModalLoader,
      helper,
      showWebhookDetails,
      getInitiatorDisplay,
      getWebhookStatusText,
    }
  }
})
</script>
